import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import type { PlaceRecord } from "../lib/types";

export default function PlaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [place, setPlace] = useState<PlaceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rawExpanded, setRawExpanded] = useState(false);

  const [draft, setDraft] = useState({
    name: "",
    addressLine1: "",
    city: "",
    state: "",
    description: "",
  });

  // Reusable loader: run on mount AND after a successful save (the update edge fn
  // returns {success:true}, not the record, so we must refetch to get fresh state
  // and reset the draft from it).
  const fetchPlace = useCallback(async () => {
    if (!id) return;
    const data = await invokeAdmin<PlaceRecord>("admin_place_get", { id });
    setPlace(data);
    setDraft({
      name: data.props.name ?? "",
      addressLine1: data.props.addressLine1 ?? "",
      city: data.props.city ?? "",
      state: data.props.state ?? "",
      description: data.props.description ?? "",
    });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await fetchPlace();
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, fetchPlace]);

  if (!id) return null;

  async function save() {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await invokeAdmin("admin_place_update", { id, props_patch: draft });
      // Update returns {success:true}, not the record — refetch to get fresh
      // state and reset the draft from it.
      await fetchPlace();
      setEditing(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    if (!place) return;
    setDraft({
      name: place.props.name ?? "",
      addressLine1: place.props.addressLine1 ?? "",
      city: place.props.city ?? "",
      state: place.props.state ?? "",
      description: place.props.description ?? "",
    });
    setEditing(false);
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm("Delete this place? This cannot be undone.")) return;
    setDeleting(true);
    setError(null);
    try {
      await invokeAdmin("admin_place_delete", { id });
      navigate("/records");
    } catch (e) {
      setError(errMsg(e));
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <p className="mt-4 text-sm text-zinc-500">Loading…</p>
      </div>
    );
  }

  if (error && !place) {
    return (
      <div>
        <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
          ← Records
        </Link>
        <div className="mt-4 rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!place) return null;

  const FIELDS: { key: keyof typeof draft; label: string; multiline?: boolean }[] = [
    { key: "name", label: "Name" },
    { key: "addressLine1", label: "Address" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "description", label: "Description", multiline: true },
  ];

  return (
    <div>
      <Link to="/records" className="text-sm text-zinc-400 hover:text-zinc-100">
        ← Records
      </Link>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{place.props.name ?? "Place"}</h1>
          <p className="mt-0.5 text-xs text-zinc-500 font-mono">{place.id}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={cancelEdit}
                className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded bg-red-800 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 space-y-4">
        {FIELDS.map(({ key, label, multiline }) => (
          <div key={key}>
            <label className="text-xs text-zinc-500 uppercase">{label}</label>
            {editing ? (
              multiline ? (
                <textarea
                  value={draft[key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              ) : (
                <input
                  type="text"
                  value={draft[key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  className="mt-1 w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              )
            ) : (
              <p className="mt-1 text-sm text-zinc-300">{draft[key] || "—"}</p>
            )}
          </div>
        ))}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-zinc-500 uppercase">Latitude</span>
            <p className="mt-1 text-sm text-zinc-300 font-mono">
              {place.props.latitude ?? place.props.lat ?? "—"}
            </p>
          </div>
          <div>
            <span className="text-xs text-zinc-500 uppercase">Longitude</span>
            <p className="mt-1 text-sm text-zinc-300 font-mono">
              {place.props.longitude ?? place.props.lng ?? place.props.lon ?? "—"}
            </p>
          </div>
        </div>

        <div>
          <span className="text-xs text-zinc-500 uppercase">Created</span>
          <p className="mt-1 text-sm text-zinc-300">{new Date(place.created).toLocaleString()}</p>
        </div>
      </div>

      <div className="mt-4">
        <button
          onClick={() => setRawExpanded((v) => !v)}
          className="text-xs text-zinc-500 hover:text-zinc-300"
        >
          {rawExpanded ? "Hide" : "Show"} raw props
        </button>
        {rawExpanded && (
          <pre className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-xs text-zinc-300 overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(place.props, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
